"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useClosePositionInfo = void 0;
const react_1 = require("react");
const interface_1 = require("./../../../interface");
const utils_1 = require("./../../../utils");
const trade_utils_lib_1 = require("trade_utils_lib");
const usePositionTradingFeeSum_1 = require("./usePositionTradingFeeSum");
/**计算浮动盈亏
 * 市价浮动盈亏（单位quote） = 仓位方向*（当前价格 - 持仓均价）*平仓数量
 * 限价浮动盈亏（单位quote） = 仓位方向*（限价价格 - 持仓均价）*平仓数量
 */
function calcFloatingPnL({ price, avgPrice, quantity, orderSide }) {
    const agv = new trade_utils_lib_1.SafeDecimal(avgPrice);
    const cur = new trade_utils_lib_1.SafeDecimal(price);
    let profitTemp = cur.minus(agv).mul(quantity);
    if (orderSide === interface_1.CustomOrderSide.BUY) {
        // 平空
        profitTemp = profitTemp.mul(-1);
    }
    return profitTemp;
}
/**计算预估平仓手续费
 * 市价平仓：预估平仓手续费 = 平仓数量 * 当前价格 * 用户当前的VIP-taker费率 * -1
 * 限价平仓：预估平仓手续费 = 平仓数量 * 限价价格 * 用户当前的VIP-maker费率 * -1
 */
function calcEstimatedClosingFee({ quantity, price, fee }) {
    return new trade_utils_lib_1.SafeDecimal(quantity).mul(price).mul(fee).mul(-1);
}
/**计算预估平仓收益
 * Estimated closing Pnl = 浮动收益 +预估平仓手续费+开仓手续费 (当前平仓qty)
 */
function calcEstimatedClosingPnL({ floatingPnL, estimatedClosingFee, openTradingFee }) {
    return new trade_utils_lib_1.SafeDecimal(floatingPnL).add(estimatedClosingFee).add(openTradingFee);
}
/**获取平仓弹窗上的预期盈亏 */
function useClosePositionInfo({ price, avgPrice, quantity, orderSide, precision, position_id, fee, qtyShort, qtyLong, }) {
    const floatingPnL = (0, react_1.useMemo)(() => {
        if (new trade_utils_lib_1.SafeDecimal(quantity).isZero() || new trade_utils_lib_1.SafeDecimal(price).isZero()) {
            return undefined;
        }
        let profit = calcFloatingPnL({
            price,
            avgPrice,
            quantity,
            orderSide,
        });
        const nP = (0, utils_1.getPnlPrecision)(profit, precision);
        profit = new trade_utils_lib_1.SafeDecimal(profit.toFixed(nP !== null && nP !== void 0 ? nP : precision, trade_utils_lib_1.SafeDecimal.ROUND_FLOOR));
        return profit;
    }, [orderSide, precision, price, quantity]);
    const [{ value, loading }, fetch] = (0, usePositionTradingFeeSum_1.usePositionTradingFeeSum)();
    const openTradingFee = (0, react_1.useMemo)(() => {
        if (value) {
            // 做空,当平仓方向为 buy 时，表示当前仓位为做空
            let val = new trade_utils_lib_1.SafeDecimal(0);
            if (orderSide === interface_1.CustomOrderSide.BUY && !new trade_utils_lib_1.SafeDecimal(qtyShort).isZero()) {
                val = new trade_utils_lib_1.SafeDecimal(quantity).div(new trade_utils_lib_1.SafeDecimal(qtyShort).abs()).mul(value.sell_fee.fee);
            }
            else if (orderSide === interface_1.CustomOrderSide.SELL && !new trade_utils_lib_1.SafeDecimal(qtyLong).isZero()) {
                // 做多
                val = new trade_utils_lib_1.SafeDecimal(quantity).div(new trade_utils_lib_1.SafeDecimal(qtyLong).abs()).mul(value.buy_fee.fee);
            }
            if (!val.isZero()) {
                const nP = (0, utils_1.getPnlPrecision)(val, precision);
                return new trade_utils_lib_1.SafeDecimal(val.toFixed(nP !== null && nP !== void 0 ? nP : precision, trade_utils_lib_1.SafeDecimal.ROUND_FLOOR));
            }
        }
    }, [value, orderSide, quantity, qtyShort, qtyLong, precision]);
    (0, react_1.useEffect)(() => {
        fetch(position_id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [position_id]);
    const estimatedClosingFee = (0, react_1.useMemo)(() => {
        var _a;
        if (price && quantity && fee) {
            const val = calcEstimatedClosingFee({
                price,
                quantity,
                fee,
            });
            return new trade_utils_lib_1.SafeDecimal(val.toFixed((_a = (0, utils_1.getPnlPrecision)(val, precision)) !== null && _a !== void 0 ? _a : precision, trade_utils_lib_1.SafeDecimal.ROUND_FLOOR));
        }
    }, [fee, precision, price, quantity]);
    const estimatedClosingPnL = (0, react_1.useMemo)(() => {
        var _a;
        if (floatingPnL && openTradingFee && estimatedClosingFee) {
            const val = calcEstimatedClosingPnL({
                floatingPnL,
                estimatedClosingFee,
                openTradingFee,
            });
            return new trade_utils_lib_1.SafeDecimal(val.toFixed((_a = (0, utils_1.getPnlPrecision)(val, precision)) !== null && _a !== void 0 ? _a : precision, trade_utils_lib_1.SafeDecimal.ROUND_FLOOR));
        }
    }, [estimatedClosingFee, floatingPnL, precision, openTradingFee]);
    return {
        floatingPnL,
        estimatedClosingFee,
        estimatedClosingPnL,
        openTradingFee,
        loading,
    };
}
exports.useClosePositionInfo = useClosePositionInfo;
